import {getLCP, getFID, getCLS, getFCP, getTTFB} from 'web-vitals';

/**
* Show web vital stats in a "lighthouse-vitals" class element.
 *
* It returns the following stats:
*   CLS (Cumulative Layout Shift)
*   FID (First Input Delay)
*   LCP (Largest Contentful Paint)
*   FCP (First Contentful Paint)
*   TTFB (Time to First Byte)
*
* For more information on each stat:
* @see https://github.com/GoogleChrome/web-vitals
 */

/**
* Defines thresholds for success for each metric, in seconds (CLS excepted).
* Currently based on web.dev's "What is a good ### score?" for each.
*
* good: The value compared with "less than" to dictate a good score
* ni: The value compared with "less than" to dictate a "needs improvement" score
*
* Poor is defined as "greater than" `ni`, and caps at double the difference between `good` and `ni`.
*
 */
const scoringMetric = {
    CLS: {
        good: 0.1,
        ni: 0.25,
    },
    FID: {
        good: 0.1,
        ni: 0.3,
    },
    LCP: {
        good: 2.5,
        ni: 4,
    },
    FCP: {
        good: 1.8,
        ni: 3.0,
    },
    TTFB: {
        good: 0.2,
        ni: 0.35,
    },
}

initializeVitals();

/**
* Uses the web-vitals functions to fetch the scores
*
* @see https://github.com/GoogleChrome/web-vitals#functions
 */
function initializeVitals() {
    /**
     * Time to First Byte
     * @see https://web.dev/ttfb/
     */
    getTTFB(buildResult);

    /**
     * Cumulative Layout Shift
     * @see https://web.dev/cls/
     */
    getCLS(buildResult);

    /**
     * First Input Delay
     * @see https://web.dev/fid/
     */
    getFID(buildResult);

    /**
     * First Contentful Paint
     * @see https://web.dev/fcp/
     */
    getFCP(buildResult);

    /**
     * Largest Contentful Paint
     * @see https://web.dev/lcp
     */
    getLCP(buildResult);
}

/**
 * This is the callback function that is fed into the web vitals functions.
 * It takes the metric and builds the DOM element that displays the score.
 *
 * @param metric The metric object payload returned from the `get` web-vitals functions.
 */
function buildResult(metric) {
    if (metric === undefined) {
        return;
    }
    // Get the rating and percentage of the score
    const score = compareScore(metric);

    // Create the element that will show the score, and add it to the vitalsElement
    const scoreModule = document.createElement("div");
    scoreModule.classList.add('score', metric.name.toLowerCase(), score.rating);

    // Add the text that shows the score percentage
    const scoreText = document.createElement("span");
    scoreText.classList.add("score-text");
    scoreText.title = score.rating + ' / Value: ' + (metric.value / 1000).toFixed(2) + ' / Goal: ' + scoringMetric[metric.name]['good'];
    scoreText.innerText = score.percent.toString();
    scoreModule.appendChild(scoreText);

    // Add the text that labels the score type, including a link to documentation on that score
    const scoreLabel = document.createElement("a");
    scoreLabel.classList.add("score-label");
    scoreLabel.href = "https://web.dev/" + metric.name.toLowerCase();
    scoreLabel.innerText = metric.name;
    scoreLabel.setAttribute('target', '_blank');
    scoreModule.appendChild(scoreLabel);

    // Element that the visual scores display will be inserted into
    let vitalsElement = document.getElementsByClassName('lighthouse-vitals')[0];

    // Handle DOM rendering
    const renderDOM = () => {
        // Re-define vitalsElement to ensure we don't end up with an undefined element
        vitalsElement = document.getElementsByClassName('lighthouse-vitals')[0];

        // Add this score to the lighthouse-vitals element
        vitalsElement.appendChild(scoreModule);

        // Check if the help text is loaded. If not, add it!
        if (document.getElementsByClassName("lighthouse-help-text").length < 1) {
            // Add handy "what is this?" link to my Confluence doc on the feature
            const helpText = document.createElement("a");
            helpText.classList.add("lighthouse-help-text");
            helpText.href = "https://corptools.atlassian.net/l/c/X3z6oegB";
            helpText.innerText = "What is this?"
            helpText.setAttribute('target', '_blank');

            vitalsElement.appendChild(helpText);
        }
    }

    // Wait until the DOM is fully loaded to render the results
    if (vitalsElement) {
        renderDOM();
    } else {
        document.addEventListener("DOMContentLoaded", function(event) {
            renderDOM();
        });
    }
}

/**
 * Compare the score to the parameters set in the scoringMetric object
 *
 * @param metric The metric object payload returned from the `get` web-vitals functions.
 * @returns Object
 */
function compareScore(metric) {
    const score = metric.value / 1000; // millisecond -> second conversion
    let scoreRating;

    // Determines the word-based verdict of the score. Used for color-coding.
    if (score < scoringMetric[metric.name].good) {
        scoreRating = 'good';
    } else if (score < scoringMetric[metric.name].ni) {
        scoreRating = 'needs-improvement';
    } else {
        scoreRating = 'poor';
    }

    // Returns the percentage-based score of the metric.
    return {
        rating: scoreRating,
        percent: getPercent(metric)
    };
}

/**
 * Determines the threshold for zero in the "poor" rating.
 * Think of this like a threshold for "how bad the score can be before we just give you zero".
 *
 * @param metric The metric object payload returned from the `get` web-vitals functions.
 */
function getMax(metric) {
    const good = scoringMetric[metric.name].good;
    const ni = scoringMetric[metric.name].ni;

    const diff = ni - good;

    // Return the max
    return (diff * 2) + ni;
}

/**
 * Calculates the percentage of the score based on the max from getMax() and the Good threshold.
 * If it's less than the Good score because it's fast, return 100.
 * If it's more than the Max score because it's slow, return 0.
 *
 * @param metric The metric object payload returned from the `get` web-vitals functions.
 * @returns percent The percentage rating of the score
 */
function getPercent(metric) {
    let score = metric.value;
    if (metric.name !== 'CLS') {
        score = score / 1000; // seconds to milliseconds conversion
    }

    const good = scoringMetric[metric.name].good;
    const max = getMax(metric);

    // Debugging functions, leaving because they're useful :)
    // console.log(metric.name + ' Max: ' + max);
    // console.log(metric.name + ' Good: ' + good);
    // console.log(metric.name + ' Range: ' + range);
    // console.log(metric.name + ' Score: ' + score);

    // Check if the score is really great (100%) or really awful (0%).
    // We don't need to calculate in either of these scenarios, so return early.
    if (score < good) {
        return 100;
    } else if (score > max) {
        return 0;
    }

    const range = max - good;

    // Multiply to convert to whole percent, round to the nearest percent, return
    return Math.round((score / range) * 100);
}